import React, { useState, useEffect, forwardRef, useMemo, useRef } from 'react';
import AcusoftViewerControl from './AcusoftViewerControl';
import Spinner from 'features/common/Spinner';
import Error from './AcusoftViewerControl/ViewerError';
import Logger from 'utils/logger';
import {
  selectAccessToken,
  selectCurrentCaseId,
  selectMatchedUrl,
  selectMaxPagePrefetch,
  selectPresentQuery,
  selectQuery,
} from 'common/selectors';
import { useSelector } from 'react-redux';
import { useFetchViewingSessionId } from '../redux/fetchViewingSessionId';
import { Ajax } from '@prizmdoc/viewer-core';
import PresentViewControl from './AcusoftViewerControl/PresentViewControl';

// eslint-disable-next-line no-undef
const unifiedCookieEnabled = process.env.REACT_APP_UNIFIED_COOKIE_ENABLED === 'true';

interface Props {
  fileId: string;
  isPreviewMode?: boolean;
  isFileToCompare?: boolean;
  isDocFinder: boolean;
  isQueryParamMatchingAnyFolder: boolean;
  startPageForGlobalPaging: string;
  hearingRoomMode?: 'private' | 'public';
  fileData?: {
    viewingSessionId?: string;
    prizmApiUrl?: string;
    viewingFilePrivate?: boolean;
    documentRotation?: number;
    passViewingSessionIdToReceivers?: boolean;
  };
  bundleSpecificFileDetails?: object;
  removeInput?: boolean;
  enableScroll?: boolean;
  searchHandler?: (searchDetails: any) => void;
  searchFunctionsHandler?: (searchFunctions: any) => void;
  isFullScreen?: boolean;
  sideSummaryOpen?: boolean;
  setSideSummaryOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  fileHasSummary?: boolean;
  showAnnotationsInTheatreMode?: boolean;
  copyable?: boolean;
  onViewerReady?: () => void;
  onViewerError?: () => void;
  waitForFirstScaleAfterFit?: boolean;
  enableSidebarAnnotations?: boolean;
  annotationToolsEnabled?: boolean;
  hidePresentButton?: boolean;
}

const AcusoftViewer = forwardRef(
  (
    {
      fileId,
      isPreviewMode,
      isFileToCompare,
      isDocFinder,
      hearingRoomMode,
      fileData,
      bundleSpecificFileDetails,
      isQueryParamMatchingAnyFolder,
      startPageForGlobalPaging,
      removeInput,
      enableScroll,
      searchHandler,
      searchFunctionsHandler,
      isFullScreen,
      sideSummaryOpen,
      setSideSummaryOpen,
      fileHasSummary,
      showAnnotationsInTheatreMode,
      copyable,
      onViewerReady,
      onViewerError,
      waitForFirstScaleAfterFit,
      enableSidebarAnnotations,
      annotationToolsEnabled,
      hidePresentButton,
    }: Props,
    ref,
  ) => {
    const [viewerOptions, setViewerOptions] = useState(fileData);
    const [viewingSessionIdError, setViewingSessionIdError] = useState(false);
    const [presentView, setPresentView] = useState('normal');
    const present = useSelector(selectPresentQuery) as 'private' | 'public' | undefined;
    const maxPagePrefetch = useSelector(selectMaxPagePrefetch);
    const currentUrl = useSelector(selectMatchedUrl);
    const caseId = useSelector(selectCurrentCaseId);
    const isPresentModePresent = present && currentUrl.includes(`${caseId}/present-view`);
    const accessToken = useSelector(selectAccessToken);
    const query = useSelector(selectQuery);

    const { fetchViewingSessionId } = useFetchViewingSessionId();

    const [updatingDoc, setUpdatingDoc] = useState(false);
    const previousViewerOptionsRef = useRef(viewerOptions);

    const fetchViewingSessionIdHandler = useMemo(
      () => () => {
        setUpdatingDoc(true);
        return fetchViewingSessionId(fileId)
          .then((res: any) => {
            const newVewerOptions = {
              viewingSessionId: res.viewingSessionId,
              prizmApiUrl: res.prizmApiUrl,
              viewingFilePrivate: res.file?.private,
              passViewingSessionIdToReceivers: res.passViewingSessionIdToReceivers,
            };
            setViewerOptions(newVewerOptions);
            setUpdatingDoc(false);
            return newVewerOptions;
          })
          .catch((res: any) => {
            Logger.ERROR('Fetching viewing session ID', res.error);
            setViewingSessionIdError(true);
            setUpdatingDoc(false);
          });
      },
      [fetchViewingSessionId, fileId],
    );

    const currentViewerOptions =
      updatingDoc && previousViewerOptionsRef.current
        ? previousViewerOptionsRef.current
        : viewerOptions;

    useEffect(() => {
      if (
        !viewerOptions?.viewingSessionId ||
        !viewerOptions?.prizmApiUrl ||
        viewerOptions?.viewingFilePrivate === undefined
      ) {
        fetchViewingSessionIdHandler();
      }
      // MOUNT
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      Ajax.setHeaders({
        Authorization: `Bearer ${accessToken}`,
      });

      if (unifiedCookieEnabled) {
        // PrizmDoc viewer-core's internal XHR doesn't set withCredentials,
        // so the tv_session cookie is never sent. Override transport to add credentials.
        Ajax.setOverrideMethod((options: any) =>
          fetch(options.url, {
            method: options.method || 'GET',
            headers: options.headers,
            body: options.body,
            credentials: 'include',
          }).then(async (res) => {
            const resp: any = {
              status: res.status,
              responseText: options.responseType === 'arraybuffer' ? await res.arrayBuffer() : await res.text(),
              getResponseHeader: (h: string) => res.headers.get(h),
              getAllResponseHeaders: () => [...res.headers.entries()].map(([k, v]) => `${k}: ${v}`).join('\r\n'),
            };
            if (!res.ok) throw { response: resp, error: { message: `${res.status} ${res.statusText}` } };
            return resp;
          }),
        );
      }
    }, [accessToken]);

    useEffect(() => {
      if (query && query.view === 'share') {
        setPresentView('share');
      } else {
        setPresentView('normal');
      }
    }, [query]);

    useEffect(() => {
      if (viewingSessionIdError && onViewerError) {
        onViewerError();
      }
    }, [viewingSessionIdError, onViewerError]);

    return (
      <div className="viewing-acusoft-viewer">
        {viewingSessionIdError ? (
          <Error />
        ) : currentViewerOptions?.viewingSessionId &&
          currentViewerOptions?.prizmApiUrl &&
          accessToken ? (
          presentView === 'share' || (query.view === 'share' && isFullScreen) ? (
            <PresentViewControl
              key={viewerOptions?.viewingSessionId}
              ref={ref}
              className="pcc-page-list"
              options={{
                documentID: viewerOptions?.viewingSessionId,
                imageHandlerUrl: viewerOptions?.prizmApiUrl,
                // maxPrefetch not a part of documentation, but was instructed by acusoft team in an email 08.07.22
                maxPrefetch: 0,
                pageRotation: viewerOptions?.documentRotation || 0,
                // viewerAssetsPath: process.env.REACT_APP_VIEWER_ASSETS,
              }}
              fileId={fileId}
              viewingFilePrivate={viewerOptions?.viewingFilePrivate}
              passViewingSessionIdToReceivers={viewerOptions?.passViewingSessionIdToReceivers}
              firstParentTriaBundleFileDetails={bundleSpecificFileDetails}
              present={present}
              removeInput={removeInput}
              fileData={fileData}
              enableScroll={enableScroll}
              isFullScreen={isFullScreen}
              isPreviewMode={isPreviewMode}
              hearingRoomMode={hearingRoomMode}
              searchHandler={searchHandler}
              searchFunctionsHandler={searchFunctionsHandler}
              sideSummaryOpen={sideSummaryOpen}
              setSideSummaryOpen={setSideSummaryOpen}
              fileHasSummary={fileHasSummary}
              fetchViewingSessionIdHandler={fetchViewingSessionIdHandler}
              isQueryParamMatchingAnyFolder={isQueryParamMatchingAnyFolder}
              startPageForGlobalPaging={startPageForGlobalPaging}
              isDocFinder={isDocFinder}
              onViewerReady={onViewerReady}
              onViewerError={onViewerError}
              hidePresentButton={hidePresentButton}
            />
          ) : (
            <AcusoftViewerControl
              key={viewerOptions?.viewingSessionId}
              ref={ref}
              className="pcc-page-list"
              options={{
                documentID: viewerOptions?.viewingSessionId,
                imageHandlerUrl: viewerOptions?.prizmApiUrl,
                // maxPrefetch not a part of documentation, but was instructed by acusoft team in an email 08.07.22
                maxPrefetch: isPresentModePresent ? 0 : parseInt(maxPagePrefetch),
                pageRotation: viewerOptions?.documentRotation || 0,
                // viewerAssetsPath: process.env.REACT_APP_VIEWER_ASSETS,
              }}
              fileId={fileId}
              viewingFilePrivate={viewerOptions?.viewingFilePrivate}
              passViewingSessionIdToReceivers={viewerOptions?.passViewingSessionIdToReceivers}
              firstParentTriaBundleFileDetails={bundleSpecificFileDetails}
              removeInput={removeInput}
              enableScroll={enableScroll}
              isFullScreen={isFullScreen}
              isQueryParamMatchingAnyFolder={isQueryParamMatchingAnyFolder}
              isDocFinder={isDocFinder}
              startPageForGlobalPaging={startPageForGlobalPaging}
              isPreviewMode={isPreviewMode}
              isFileToCompare={isFileToCompare}
              hearingRoomMode={hearingRoomMode}
              searchHandler={searchHandler}
              searchFunctionsHandler={searchFunctionsHandler}
              sideSummaryOpen={sideSummaryOpen}
              fileHasSummary={fileHasSummary}
              fetchViewingSessionIdHandler={fetchViewingSessionIdHandler}
              showAnnotationsInTheatreMode={showAnnotationsInTheatreMode}
              copyable={copyable}
              fileData={fileData}
              waitForFirstScaleAfterFit={waitForFirstScaleAfterFit}
              enableSidebarAnnotations={enableSidebarAnnotations}
              annotationToolsEnabled={annotationToolsEnabled}
              onViewerReady={onViewerReady}
              onViewerError={onViewerError}
              hidePresentButton={hidePresentButton}
            />
          )
        ) : (
          <Spinner />
        )}
      </div>
    );
  },
);

export default AcusoftViewer;
